import { APIv2Params } from 'types/apiv2'

/**
 * Converts an APIv2Params object to a query string.
 *
 * It iterates over all entries in the object and builds a string representation in
 * the format 'key=value'. If the value is an array, it is converted to a comma-separated string.
 * If the value is an object (like for complex filters), each entry of the object is converted to a
 * string in the format 'key=value' and then joined with commas.
 *
 * All keys and values are URL-encoded.
 */
const toAPIv2QueryString = <T>(params: APIv2Params<T>): string => {
  const entries = Object.entries(params).map(([key, value]) => {
    key = encodeURIComponent(key)

    if (Array.isArray(value)) {
      // Check if the array consists of objects
      if (typeof value[0] === 'object' && value[0] !== null) {
        return `${key}=${value
          .map((obj: { [key: string]: string }) =>
            Object.entries(obj)
              .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
              .join(','),
          )
          .join(';')}` // Separate different objects with a semicolon or other character
      } else {
        return `${key}=${value.map((item) => encodeURIComponent(item)).join(',')}`
      }
    } else if (typeof value === 'object' && value !== null) {
      const obj = value as { [key: string]: string } // added this line

      return `${key}=${Object.entries(obj)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join(',')}`
    } else {
      return `${key}=${encodeURIComponent(value)}`
    }
  })

  return entries.join('&')
}

export default toAPIv2QueryString
